import { useEffect, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { X, CreditCard, Gift, CheckCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import useFeaturePing from '../../hooks/useFeaturePing';
import trackEvent from '../../services/trackEvent';

let stripePromise = null;
function getStripePromise() {
    if (!stripePromise) {
        stripePromise = api.get('/stripe/config.php')
            .then(({ data }) => loadStripe(data.data.public_key));
    }
    return stripePromise;
}

const DEFAULT_PLANS = [
    { duree: '1m', label: '1 mois', prix: '3', prixMois: '3' },
    { duree: '6m', label: '6 mois', prix: '10', prixMois: '1,67', popular: true },
    { duree: '12m', label: '12 mois', prix: '18', prixMois: '1,50' },
];

function formatPrix(v) {
    const n = parseFloat(v);
    return Number.isInteger(n) ? n.toString() : n.toFixed(2).replace('.', ',');
}

function buildPlans(pricing) {
    if (!pricing?.price_1m) return DEFAULT_PLANS;
    const p1 = parseFloat(pricing.price_1m);
    const p6 = parseFloat(pricing.price_6m);
    const p12 = parseFloat(pricing.price_12m);
    return [
        { duree: '1m', label: '1 mois', prix: formatPrix(p1), prixMois: formatPrix(p1) },
        { duree: '6m', label: '6 mois', prix: formatPrix(p6), prixMois: formatPrix(p6 / 6), popular: true },
        { duree: '12m', label: '12 mois', prix: formatPrix(p12), prixMois: formatPrix(p12 / 12) },
    ];
}

/**
 * Modal tout-en-un : choix de durée + checkout Stripe embedded.
 *
 * Props:
 *   scopeId         — ID du scope
 *   scopeLabel      — label affiché (ex: "Fonction publique territoriale")
 *   onClose         — fermer la modal
 *   extraBody       — params supplémentaires pour create-session (ex: { user_thematique_id })
 *   ctaLabel        — label du bouton CTA (défaut : « Débloquer <durée> · <prix> », ou « Prolonger … » si accès actif)
 *   subtitle        — sous-titre optionnel
 *   pricing         — { price_1m, price_6m, price_12m } du scope (optionnel, fallback sur defaults)
 *   currentAccessEnd — date YYYY-MM-DD de fin d'accès actuel (si actif).
 *                      Si fourni → bannière prolongation affichée.
 *   source          — provenance de l'ouverture (mesure du parcours : 'melange', 'fin_quiz'…)
 */
export default function SubscriptionModal({ scopeId, scopeLabel, onClose, extraBody, ctaLabel, subtitle, pricing, currentAccessEnd, source }) {
    useFeaturePing('subscription_modal'); // monté = ouvert (compteur d'usage admin)
    // Parcours : offres affichées, avec leur provenance (une fois par ouverture).
    useEffect(() => {
        trackEvent('paywall_shown', { source: source || 'autre', scopeId, meta: currentAccessEnd ? { prolongation: true } : undefined });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const [selectedDuree, setSelectedDuree] = useState('6m');
    // Parrainage (migration 178) : rempli par /auth/me.php.
    const referral = useAuthStore(s => s.user?.referral) || null;
    // Code de parrainage saisi ici, avant le paiement (le webhook lit le parrain au paiement).
    const [refOpen, setRefOpen] = useState(false);
    const [refCode, setRefCode] = useState('');
    const [refMsg, setRefMsg] = useState(null);
    const [refLoading, setRefLoading] = useState(false);
    const submitReferral = async () => {
        if (!refCode.trim()) return;
        setRefLoading(true); setRefMsg(null);
        try {
            await api.post('/user/referral.php', { code: refCode.trim() });
            await useAuthStore.getState().fetchProfile(); // fait apparaître le bandeau « 7 jours offerts »
            setRefOpen(false);
        } catch (err) {
            setRefMsg(err?.response?.data?.message || 'Code refusé.');
        }
        setRefLoading(false);
    };
    const [clientSecret, setClientSecret] = useState(null);
    const [error, setError] = useState(null);
    const [loadingCheckout, setLoadingCheckout] = useState(false);

    // Gift mode
    const [isGift, setIsGift] = useState(false);
    const [giftEmail, setGiftEmail] = useState('');

    // Redeem gift code
    const [redeemCode, setRedeemCode] = useState('');
    const [redeemLoading, setRedeemLoading] = useState(false);
    const [redeemResult, setRedeemResult] = useState(null); // { success, message, data }
    const fetchProfile = useAuthStore(s => s.fetchProfile);

    // Simulate mode
    const isDev = import.meta.env.DEV || new URLSearchParams(window.location.search).has('simulate');
    const [simulating, setSimulating] = useState(false);

    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleChoose = async (duree) => {
        setSelectedDuree(duree);
        setError(null);
        setLoadingCheckout(true);
        const evt = { source: source || 'autre', scopeId, meta: { duree, gift: isGift } };
        trackEvent('paywall_plan_clicked', evt);

        try {
            const body = { scope_id: scopeId, duree, ...extraBody };
            if (isGift) {
                body.is_gift = true;
                if (giftEmail) body.gift_email = giftEmail;
            }
            const { data } = await api.post('/stripe/create-session.php', body);
            setClientSecret(data.data.client_secret);
            trackEvent('checkout_started', evt);
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur Stripe');
        }
        setLoadingCheckout(false);
    };

    const handleSimulate = async (duree) => {
        setSimulating(true);
        setError(null);
        try {
            const body = { scope_id: scopeId, duree, simulate: true, ...extraBody };
            if (isGift) {
                body.is_gift = true;
                if (giftEmail) body.gift_email = giftEmail;
            }
            const { data } = await api.post('/stripe/create-session.php', body);
            if (data.data.simulated) {
                setTimeout(() => { window.location.href = data.data.redirect_url; }, 1500);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Erreur simulation');
            setSimulating(false);
        }
    };

    const handleRedeemGift = async () => {
        if (!redeemCode.trim()) return;
        setRedeemLoading(true);
        setError(null);
        try {
            const { data } = await api.post('/gift/redeem.php', { code: redeemCode.trim() });
            if (data.success) {
                setRedeemResult(data.data);
                // Force refresh pour récupérer le selectedScopes auto-ajouté par le backend
                // (le merge useDashboardPrefs favorise localStorage donc on doit le purger).
                localStorage.removeItem('monquiz_dashboard_prefs');
                await fetchProfile();
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Code invalide');
        }
        setRedeemLoading(false);
    };

    const PLANS = buildPlans(pricing);
    const plan = PLANS.find(p => p.duree === selectedDuree);

    return (
        <div className="checkout-modal-overlay" onClick={onClose}>
            <div className="checkout-modal checkout-modal--wide" onClick={(e) => e.stopPropagation()}>
                <div className="checkout-modal__header">
                    <span className="checkout-modal__title">
                        {clientSecret ? `Paiement — ${plan?.label} (${plan?.prix}€)` : (scopeLabel || 'Abonnement')}
                    </span>
                    <button className="checkout-modal__close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>
                <div className="checkout-modal__body">
                    {error && <div className="alert alert-error" style={{ margin: '16px' }}>{error}</div>}

                    {simulating && (
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <div className="spinner" style={{ margin: '0 auto 12px' }} />
                            <p><strong>Paiement simulé</strong> — Redirection...</p>
                        </div>
                    )}

                    {!clientSecret && !simulating && (
                        <div className="sub-modal-plans">
                            {subtitle && <div className="sub-modal-subtitle">{subtitle}</div>}
                            {/* Promo "gratuit jusqu'au X" : transparence avant achat — la thématique
                                est déjà accessible sans payer jusqu'à la date. */}
                            {pricing?.free_until && new Date(pricing.free_until + 'T23:59:59') >= new Date() && (
                                <div style={{
                                    background: 'rgba(5, 150, 105, 0.08)',
                                    border: '1px solid rgba(5, 150, 105, 0.35)',
                                    color: '#059669', borderRadius: 10,
                                    padding: '10px 14px', margin: '0 0 14px', fontSize: '0.9rem',
                                }}>
                                    🎁 Bon à savoir : cette thématique est <strong>gratuite pour tous jusqu'au{' '}
                                    {new Date(pricing.free_until + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
                                    Un abonnement n'est utile que pour continuer après cette date.
                                </div>
                            )}
                            {/* Parrainage : la semaine offerte au filleul et les semaines de parrain
                                en réserve s'ajoutent à cet achat (webhook, lib/Referral.php). */}
                            {!isGift && referral && (referral.bonus_pending || referral.weeks_reserved > 0) && (
                                <div style={{
                                    background: 'rgba(124, 58, 237, 0.08)',
                                    border: '1px solid rgba(124, 58, 237, 0.35)',
                                    color: 'var(--primary)', borderRadius: 10,
                                    padding: '10px 14px', margin: '0 0 14px', fontSize: '0.9rem',
                                }}>
                                    🎁 {referral.bonus_pending && <>Grâce au parrainage{referral.referrer_prenom ? ` de ${referral.referrer_prenom}` : ''}, <strong>7 jours offerts</strong> s'ajoutent à cet abonnement.{' '}</>}
                                    {referral.weeks_reserved > 0 && <>Tes <strong>{referral.weeks_reserved * 7} jours de parrainage en réserve</strong> s'y ajoutent aussi.</>}
                                </div>
                            )}
                            {/* Bannière prolongation : si l'utilisateur a déjà un accès actif,
                                on lui rappelle que le temps acheté s'ajoute à la fin de l'accès actuel. */}
                            {currentAccessEnd && new Date(currentAccessEnd) >= new Date() && !isGift && (
                                <div style={{
                                    background: 'rgba(34, 197, 94, 0.08)',
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: 8,
                                    padding: '8px 12px',
                                    margin: '0 0 12px',
                                    fontSize: '0.85em',
                                    color: 'var(--text)',
                                }}>
                                    ✅ <strong>Prolongation :</strong> votre nouvel accès démarrera <strong>après</strong> votre accès actuel (le {new Date(currentAccessEnd + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}). Aucun jour perdu.
                                </div>
                            )}
                            <div className="sub-modal-grid">
                                {PLANS.map((p) => (
                                    <button
                                        key={p.duree}
                                        className={`sub-modal-plan ${p.popular ? 'sub-modal-plan--popular' : ''} ${selectedDuree === p.duree ? 'sub-modal-plan--active' : ''}`}
                                        onClick={() => setSelectedDuree(p.duree)}
                                        disabled={loadingCheckout}
                                    >
                                        {p.popular && <span className="sub-modal-badge">Populaire</span>}
                                        <span className="sub-modal-duree">{p.label}</span>
                                        <span className="sub-modal-prix">{p.prix}&euro;</span>
                                        <span className="sub-modal-mois">{p.prixMois}&euro;/mois</span>
                                        {pricing?.qrc_quota?.[p.duree] > 0 && (
                                            <span className="sub-modal-qrc" title="Corrections de réponses rédigées ajoutées à ton solde, sans date limite">
                                                + {pricing.qrc_quota[p.duree]} corrections rédigées
                                            </span>
                                        )}
                                        {pricing?.notions_offertes?.[p.duree] > 0 && (
                                            <span className="sub-modal-qrc" title="Notions à la carte (fiche + QCM) à choisir dans cette thématique, acquises définitivement">
                                                + {pricing.notions_offertes[p.duree]} notion{pricing.notions_offertes[p.duree] > 1 ? 's' : ''} offerte{pricing.notions_offertes[p.duree] > 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                            {/* Gift toggle */}
                            <div className="sub-modal-gift-toggle">
                                <button
                                    type="button"
                                    className={`sub-modal-gift-btn ${isGift ? 'sub-modal-gift-btn--active' : ''}`}
                                    onClick={() => setIsGift(!isGift)}
                                >
                                    <Gift size={16} />
                                    {isGift ? 'Mode cadeau actif' : 'Offrir en cadeau'}
                                </button>
                                {isGift && (
                                    <div className="sub-modal-gift-email">
                                        <input
                                            type="email"
                                            placeholder="Email du destinataire (optionnel)"
                                            value={giftEmail}
                                            onChange={(e) => setGiftEmail(e.target.value)}
                                            className="sub-modal-gift-input"
                                        />
                                        <p className="sub-modal-gift-hint">
                                            Un code unique sera généré. Vous pourrez aussi l'envoyer vous-même.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <button
                                className="btn btn-primary sub-modal-cta"
                                onClick={() => selectedDuree && handleChoose(selectedDuree)}
                                disabled={!selectedDuree || loadingCheckout}
                            >
                                {loadingCheckout ? (
                                    <><div className="spinner spinner--sm" /> Chargement...</>
                                ) : isGift ? (
                                    <><Gift size={16} /> Acheter le code cadeau</>
                                ) : (
                                    // Paiement unique : le bouton dit ce qu'on achète (durée et prix),
                                    // pas « S'abonner », qui laisse croire à une reconduction.
                                    <><CreditCard size={16} /> {ctaLabel || (plan
                                        ? `${currentAccessEnd && new Date(currentAccessEnd) >= new Date() ? 'Prolonger' : 'Débloquer'} ${plan.label} · ${plan.prix} €`
                                        : 'Débloquer l’accès complet')}</>
                                )}
                            </button>
                            {isDev && selectedDuree && (
                                <button
                                    className="btn btn-sm"
                                    style={{ display: 'block', margin: '8px auto 0', fontSize: '0.75em', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 12px', cursor: 'pointer' }}
                                    onClick={() => handleSimulate(selectedDuree)}
                                >
                                    Simuler (dev)
                                </button>
                            )}
                            <p className="sub-modal-note">Paiement unique, sans renouvellement automatique.</p>
                            <div className="sub-modal-payment-logos">
                                <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Visa"><rect width="38" height="24" rx="3" fill="#1A1F71"/><path d="M15.6 16.2l1.5-9.4h2.4l-1.5 9.4h-2.4zm10.2-9.2c-.5-.2-1.2-.4-2.1-.4-2.3 0-4 1.2-4 3 0 1.3 1.2 2 2.1 2.4.9.4 1.2.7 1.2 1.1 0 .6-.7.9-1.4.9-1 0-1.5-.1-2.3-.5l-.3-.2-.3 2c.6.3 1.6.5 2.7.5 2.5 0 4.1-1.2 4.1-3.1 0-1-.6-1.8-2-2.4-.8-.4-1.3-.7-1.3-1.1 0-.4.4-.8 1.3-.8.7 0 1.3.2 1.7.3l.2.1.4-1.8zM30 16.2l1.9-5.2.5 2.6.3 1.4.2 1.2H30zm3.7-9.4h-1.9c-.6 0-1 .2-1.3.7l-3.6 8.7h2.5l.5-1.4h3.1l.3 1.4H35l-1.3-9.4zM13.5 6.8l-2.3 6.4-.2-1.3c-.4-1.4-1.7-3-3.2-3.7l2.1 7.9h2.5l3.7-9.3h-2.6z" fill="#fff"/><path d="M9.2 6.8H5.6l-.1.2c3 .8 5 2.6 5.8 4.8l-.8-4.2c-.1-.5-.5-.7-1.3-.8z" fill="#F9A533"/></svg>
                                <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Mastercard"><rect width="38" height="24" rx="3" fill="#252525"/><circle cx="15" cy="12" r="7" fill="#EB001B"/><circle cx="23" cy="12" r="7" fill="#F79E1B"/><path d="M19 7.3c1.7 1.2 2.8 3.2 2.8 5.4s-1.1 4.2-2.8 5.4c-1.7-1.2-2.8-3.2-2.8-5.4s1.1-4.2 2.8-5.4z" fill="#FF5F00"/></svg>
                                <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="CB"><rect width="38" height="24" rx="3" fill="#1E5CA6"/><text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="Arial">CB</text></svg>
                                <svg viewBox="0 0 38 24" width="38" height="24" role="img" aria-label="Apple Pay"><rect width="38" height="24" rx="3" fill="#000"/><text x="19" y="15" textAnchor="middle" fill="#fff" fontSize="7" fontWeight="500" fontFamily="Arial"> Pay</text></svg>
                                <svg viewBox="0 0 24 24" width="16" height="16" role="img" aria-label="Paiement sécurisé"><path d="M12 1L3 5v6c0 5.5 3.8 10.7 9 12 5.2-1.3 9-6.5 9-12V5l-9-4zm0 10.99h7c-.5 4.2-3.1 7.9-7 9.01V12H5V6.3l7-3.1v8.79z" fill="#4CAF50"/></svg>
                                <span className="sub-modal-secure-text">Paiement sécurisé</span>
                            </div>

                            {/* Redeem gift code */}
                            <div className="sub-modal-redeem">
                                <div className="sub-modal-redeem-divider"><span>ou</span></div>
                                <div className="sub-modal-redeem-row">
                                    <input
                                        type="text"
                                        placeholder="Entrer un code cadeau"
                                        value={redeemCode}
                                        onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
                                        className="sub-modal-gift-input"
                                        style={{ flex: 1, fontFamily: 'monospace', letterSpacing: 1 }}
                                        onKeyDown={(e) => e.key === 'Enter' && handleRedeemGift()}
                                    />
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleRedeemGift}
                                        disabled={redeemLoading || !redeemCode.trim()}
                                        style={{ whiteSpace: 'nowrap', padding: '10px 16px' }}
                                    >
                                        {redeemLoading ? <Loader2 size={16} className="spin" /> : <><Gift size={16} /> Activer</>}
                                    </button>
                                </div>
                                {!isGift && referral?.can_enter_code && (
                                    refOpen ? (
                                        <div className="sub-modal-redeem-row" style={{ marginTop: 10 }}>
                                            <input
                                                type="text" autoFocus maxLength={16}
                                                placeholder="Code de parrainage (AMI-…)"
                                                value={refCode}
                                                onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                                                className="sub-modal-gift-input"
                                                style={{ flex: 1, fontFamily: 'monospace', letterSpacing: 1 }}
                                                onKeyDown={(e) => e.key === 'Enter' && submitReferral()}
                                            />
                                            <button className="btn btn-secondary" onClick={submitReferral}
                                                disabled={refLoading || !refCode.trim()} style={{ whiteSpace: 'nowrap', padding: '10px 16px' }}>
                                                {refLoading ? <Loader2 size={16} className="spin" /> : 'Valider'}
                                            </button>
                                        </div>
                                    ) : (
                                        <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8 }} onClick={() => setRefOpen(true)}>
                                            J’ai un code de parrainage
                                        </button>
                                    )
                                )}
                                {refMsg && <p style={{ color: 'var(--danger)', fontSize: '0.85rem', margin: '6px 0 0' }}>{refMsg}</p>}
                            </div>
                        </div>
                    )}

                    {/* Gift code redeemed successfully */}
                    {redeemResult && (
                        <div style={{ textAlign: 'center', padding: '32px 20px' }}>
                            <CheckCircle size={48} style={{ color: '#10b981', marginBottom: 16 }} />
                            <h3 style={{ marginBottom: 8 }}>{redeemResult.already_active ? 'Tout est bon !' : 'Cadeau activé !'}</h3>
                            <p style={{ color: 'var(--text-muted)', marginBottom: 8 }}>
                                <strong>{redeemResult.scope_label}</strong>
                            </p>
                            {redeemResult.fin && (
                                <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>
                                    {redeemResult.already_active ? 'Actif' : 'Accès'} jusqu'au {new Date(redeemResult.fin + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                </p>
                            )}
                            <button className="btn btn-primary" onClick={onClose}>Fermer</button>
                        </div>
                    )}

                    {clientSecret && !simulating && !redeemResult && (
                        <div>
                            <button
                                className="sub-modal-back"
                                onClick={() => { setClientSecret(null); setSelectedDuree(null); setError(null); }}
                            >
                                ← Changer de formule
                            </button>
                            <EmbeddedCheckoutProvider stripe={getStripePromise()} options={{ clientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
